import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { Button } from 'components/ui/button';
import { meetings, type MobileMeetingAccess } from 'services/api/meetingsClient';
import type { CallSurfaceHandle, CallSurfaceProps } from '../types';
import { getPanelTokenFromUrl } from '../shared/panelUrls';
import { CustomDailyCallSurface } from './CustomDailyCallSurface';

/** Shared server admission lets a native host admit web guests without a second Daily peer.
 * No browser Daily object is created until the host has authorized this attendee. */
export const BackendAdmittedWebCallSurface = forwardRef<CallSurfaceHandle, CallSurfaceProps>((props, ref) => {
  const [access, setAccess] = useState<MobileMeetingAccess>();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const current = useRef(props); current.current = props;
  const generation = useRef(0);
  const mounted = useRef(true);
  const inner = useRef<CallSurfaceHandle>(null);
  const accessRef = useRef(access); accessRef.current = access;
  const prepare = useCallback(async () => {
    const { meetingId, joinInfo, attendee } = current.current;
    return meetings.prepareMobileAccess(meetingId, {
      panel_token: getPanelTokenFromUrl(joinInfo.messages_panel?.url),
      ...(attendee.guestToken ? { guest_token: attendee.guestToken } : {}),
      ...(attendee.breakoutToken ? { breakout_token: attendee.breakoutToken } : {}),
    });
  }, []);
  const cancel = useCallback(() => {
    generation.current += 1;
    if (accessRef.current) void meetings.cancelMobileAccess(current.current.meetingId, accessRef.current.lifecycle_token).catch(() => undefined);
  }, []);
  const accept = useCallback((next: MobileMeetingAccess) => {
    setAccess(next);
    if (next.state === 'removed') current.current.onLeft('removed');
    if (next.state === 'denied') current.current.onAccessDenied();
    if (next.state === 'waiting') current.current.onWaiting();
  }, []);
  const request = async () => {
    if (busy) return;
    const version = ++generation.current;
    setBusy(true); setError('');
    try {
      const next = await prepare();
      if (mounted.current && generation.current === version) accept(next);
      else void meetings.cancelMobileAccess(current.current.meetingId, next.lifecycle_token).catch(() => undefined);
    } catch { if (mounted.current && generation.current === version) setError('Unable to request admission. Please try again.'); }
    finally { if (mounted.current && generation.current === version) setBusy(false); }
  };
  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; cancel(); };
  }, [cancel]);
  useEffect(() => {
    if (access?.state !== 'waiting') return;
    let stopped = false;
    let timer: ReturnType<typeof setTimeout>;
    const version = generation.current;
    const poll = async () => {
      try {
        const next = await meetings.getMobileAccess(current.current.meetingId, access.lifecycle_token);
        if (stopped || generation.current !== version) return;
        if (next.ended || next.cancelled || next.state === 'left' || next.state === 'removed') { current.current.onLeft('removed'); return; }
        if (next.state === 'denied') { accept(next); return; }
        if (next.state === 'admitted') {
          const granted = await prepare();
          if (!stopped && generation.current === version) accept(granted);
          return;
        }
      } catch { if (!stopped) setError('Unable to check admission. Retrying…'); }
      if (!stopped) timer = setTimeout(poll, 3000);
    };
    timer = setTimeout(poll, 1500);
    return () => { stopped = true; clearTimeout(timer); };
  }, [access, accept, prepare]);
  const leave = useCallback(() => {
    cancel();
    if (inner.current) inner.current.leave(); else current.current.onLeft('left');
  }, [cancel]);
  useImperativeHandle(ref, () => ({
    leave, getLocalParticipantId: () => inner.current?.getLocalParticipantId() || '',
    openMessages: target => inner.current?.openMessages(target), isMessagesOpen: () => inner.current?.isMessagesOpen(),
  }), [leave]);
  if (access?.state === 'admitted' && access.url && access.token) {
    return <CustomDailyCallSurface ref={inner} {...props} joinInfo={{ ...props.joinInfo, url: access.url, token: access.token, requires_admission: false,
      can_start_transcription: access.can_start_transcription || props.joinInfo.can_start_transcription, poll_panel: access.poll_panel || props.joinInfo.poll_panel }}
      onJoined={id => { props.onJoined(id); props.onAccessGranted(id); }}
      onJoinInfoRefresh={next => {
        if (!next.requires_admission && next.token) setAccess(previous => previous ? { ...previous, url: next.url, token: next.token || undefined, poll_panel: next.poll_panel } : previous);
        props.onJoinInfoRefresh?.(next);
      }} onLeft={reason => { cancel(); props.onLeft(reason); }} />;
  }
  return <div className="flex h-full flex-col items-center justify-center gap-4 p-5 text-center text-white" data-testid="backend-call-admission">
    <h1 className="text-xl font-semibold">{props.activeMeeting?.title || 'WiseFriend meeting'}</h1>
    <p>{access?.state === 'waiting' ? 'Waiting for the host to admit you.' : 'Request access before joining this meeting.'}</p>
    {error && <p role="alert" className="text-red-200">{error}</p>}
    <Button disabled={busy || access?.state === 'waiting'} onClick={() => void request()}>{busy ? 'Requesting…' : 'Request to join'}</Button>
    <Button variant="secondary" onClick={leave}>Leave meeting</Button>
  </div>;
});
BackendAdmittedWebCallSurface.displayName = 'BackendAdmittedWebCallSurface';
